'use client';

import {
  useState,
  useCallback,
  useEffect,
} from 'react';

import {
  useS3Upload,
  s3Upload,
} from './useS3Upload';
import { S3BucketDestination } from '../constants';
import { tryCatch } from '../utils/tryCatch';

type Props = {
  destination: S3BucketDestination;
};

export const uploadImages = (destination: string) => (files: File[]) => {
  const pendingUploads = files.map((file) => (
    s3Upload({
      destination,
      file,
      fileName: file.name,
      type: file.type,
    })
  ));

  return tryCatch(() => Promise.all(pendingUploads));
};

export const useUploadImages = ({
  destination,
}: Props) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedImages, setUploadImages] = useState<string[]>([]);
  const {
    data,
    execute: s3UploadExecute,
  } = useS3Upload();

  const execute = useCallback(async (files: File[]) => {
    setIsLoading(true);
    setIsSuccess(false);
    const pendingUploads = files.map((file) => (
      s3UploadExecute({
        destination,
        file,
        fileName: file.name,
        type: file.type,
      })
    ));
    try {
      await Promise.all(pendingUploads);
      setIsSuccess(true);
    } catch (_error) {
      setError(_error);
    }
    setIsLoading(false);
  }, [s3UploadExecute, destination]);

  const reset = useCallback(() => {
    setIsSuccess(false);
  }, []);

  useEffect(() => {
    if (data) {
      setUploadImages((prev) => {
        prev.push(data);
        return prev;
      });
    }
  }, [data]);

  return {
    data: uploadedImages,
    execute,
    error,
    isSuccess,
    isLoading,
    reset,
  };
};
